import LayoutCenter from "@/components/LayoutCenter";
import LogoWithTextCentered from "@/components/LogoWithTextCentered";
import { Alert } from "@/components/ui/alert";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Head, Link, router } from "@inertiajs/react";
import { AlertCircle, ArrowRightIcon, CheckIcon } from "lucide-react";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

type Inputs = {
  email: string;
  password: string;
  password_confirmation: string;
  terms: boolean;
};

type PageData = {
  error: string;
  notice: string;
  please_join_waitlist?: boolean;
  on_waitlist?: boolean;
  form_errors?: {
    email: string[];
    password: string[];
    password_confirmation: string[];
  };
};

export default function Session({
  error,
  notice,
  form_errors,
  please_join_waitlist,
  on_waitlist,
}: PageData) {
  const { register, handleSubmit, getValues } = useForm<Inputs>();
  const [termsChecked, setTermsChecked] = useState(false);

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    router.post("/sign_up", data);
  };

  if (please_join_waitlist) {
    return (
      <LayoutCenter>
        <Head title="Waitlist" />
        <div className="flex flex-col items-center gap-4">
          <p>We have not fully released the app to everyone yet.</p>
          <Link href="/sign_up" className={buttonVariants({ size: "lg" })}>
            Join the waitlist <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>
      </LayoutCenter>
    );
  }

  if (on_waitlist) {
    const email = getValues("email");
    return (
      <LayoutCenter>
        <Head title="Waitlist" />
        <div className="flex flex-col items-center gap-4">
          {email && <p>Hi {email}!</p>}
          <p>You are already on the waitlist. We will notify you when Teeba is ready.</p>
        </div>
      </LayoutCenter>
    );
  }

  return (
    <LayoutCenter>
      <Head title="Sign Up" />
      {error && (
        <Alert variant="destructive" className="mb-8 max-w-sm">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        </Alert>
      )}
      {notice && (
        <Alert className="mb-8 max-w-sm">
          <div className="flex items-center gap-2">
            <CheckIcon className="h-4 w-4" />
            <p className="text-sm">{notice}</p>
          </div>
        </Alert>
      )}
      {on_waitlist && (
        <Alert variant="destructive" className="mb-8 max-w-sm">
          <div className="">
            <p className="text-sm">
              We are not accepting new users yet. Please join the waitlist by going to{" "}
              <Link href="/sign_up" className="underline">
                this page
              </Link>
              .
            </p>
          </div>
        </Alert>
      )}

      <LogoWithTextCentered className="h-12 w-auto my-1 fill-foreground mb-8" />

      <Card className="mx-auto max-w-sm w-full">
        <CardHeader>
          <CardTitle className="text-2xl">Sign up</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required {...register("email")} />
                {form_errors?.email && (
                  <p className="text-sm text-destructive">{form_errors.email.join(", ")}</p>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center">
                  <Label htmlFor="password">Password</Label>
                </div>
                <Input id="password" type="password" required {...register("password")} />
                {form_errors?.password && (
                  <p className="text-sm text-destructive">{form_errors.password.join(", ")}</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center">
                  <Label htmlFor="password">Password Confirmation</Label>
                </div>
                <Input
                  id="password_confirmation"
                  type="password"
                  required
                  {...register("password_confirmation")}
                />
                {form_errors?.password_confirmation && (
                  <p className="text-sm text-destructive">
                    {form_errors.password_confirmation.join(", ")}
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <Checkbox
                  id="terms"
                  {...register("terms")}
                  checked={termsChecked}
                  onCheckedChange={() => setTermsChecked(!termsChecked)}
                />
                <p className="text-sm text-muted-foreground">
                  I agree to the{" "}
                  <a
                    href="https://www.teeba-calculator.com/terms-of-use"
                    className="underline"
                    target="_blank"
                  >
                    Terms of Use
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://www.teeba-calculator.com/privacy-policy"
                    className="underline"
                    target="_blank"
                  >
                    Privacy Policy
                  </a>
                </p>
              </div>

              <div className="flex justify-center">
                <Button type="submit" className="w-full" disabled={!termsChecked}>
                  Sign up
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </LayoutCenter>
  );
}
